import { useCallback, useEffect, useRef, useState } from 'react';
import { useSettings } from '../../settings/SettingsContext';
import { canSpeak, speak, stopSpeaking } from '../../games/speak';
import { playCorrectPing } from '../../games/ping';
import type { TimerHoldConfig } from '../types';

export default function TimerHold({ config }: { config: TimerHoldConfig }) {
  const { settings } = useSettings();
  const [round, setRound] = useState(1);
  const [remaining, setRemaining] = useState(config.holdSeconds);
  const [holding, setHolding] = useState(false);
  const tickRef = useRef<number | null>(null);

  const playPrompt = useCallback(() => {
    if (canSpeak()) {
      speak(config.promptText, { voiceName: settings.ttsVoice, rate: settings.ttsRate });
    }
  }, [config.promptText, settings.ttsVoice, settings.ttsRate]);

  useEffect(() => () => {
    stopSpeaking();
    if (tickRef.current !== null) window.clearInterval(tickRef.current);
  }, []);

  function start() {
    if (holding) return;
    setHolding(true);
    setRemaining(config.holdSeconds);
    playPrompt();
    tickRef.current = window.setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          if (tickRef.current !== null) {
            window.clearInterval(tickRef.current);
            tickRef.current = null;
          }
          setHolding(false);
          if (settings.correctPingEnabled) playCorrectPing();
          setRound((rd) => rd + 1);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
  }

  function cancel() {
    if (tickRef.current !== null) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
    setHolding(false);
    setRemaining(config.holdSeconds);
  }

  const display = holding ? remaining : config.holdSeconds;
  const ofRounds = config.rounds > 0 ? ` of ${config.rounds}` : '';

  return (
    <div className="card concept-timer">
      <div className="muted">Round {round}{ofRounds}</div>
      <div className="concept-prompt-big">{config.promptText}</div>
      <div className="concept-timer-display" aria-live="polite">{display}s</div>
      <div className="toolbar" style={{ justifyContent: 'center' }}>
        {!holding ? (
          <button className="btn" onClick={start} aria-label="Start hold">Start</button>
        ) : (
          <button className="btn secondary" onClick={cancel} aria-label="Cancel hold">Cancel</button>
        )}
      </div>
    </div>
  );
}
